import {
  Badge,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  SidebarFooter,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@osteopro/ui';
import { Building2, CreditCard } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';
import { useEntitlements } from '../../hooks/api/use-plans.js';
import { useOrganization } from '../../lib/auth-provider.js';
import { CabinetLabel, CabinetTile } from './SidebarCabinet.js';

/**
 * Le bouton épouse son contenu : la date de version tient au-dessus du nom.
 * Repliée en icône, la pastille redevient un carré et ce texte disparaît avec le libellé.
 */
const cabinetButtonClass =
  'h-auto min-h-12 group-data-[collapsible=icon]:h-8! group-data-[collapsible=icon]:min-h-0!';

/** Pied de la barre latérale : le cabinet actif, son changement, et l'offre. */
export function SidebarCabinetFooter() {
  const { t } = useTranslation();
  const { organization, organizations, canSwitch, switchOrganization } = useOrganization();
  const cabinetName = organization?.name ?? t('shell:sidebar.no_cabinet');

  return (
    <SidebarFooter className="border-t p-2">
      <SidebarMenu>
        <SidebarMenuItem>
          {canSwitch ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  tooltip={cabinetName}
                  className={cabinetButtonClass}
                  data-testid="button-sidebar-organization"
                >
                  <CabinetTile />
                  <CabinetLabel name={cabinetName} hint={t('shell:sidebar.switch_cabinet')} />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="top" align="start" className="w-56">
                <DropdownMenuLabel>{t('shell:sidebar.switch_cabinet')}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {organizations.map((item) => (
                  <DropdownMenuItem
                    key={item.id}
                    onClick={() => void switchOrganization(item.id)}
                    data-testid={`menu-organization-${item.id}`}
                  >
                    <Building2 className="mr-2 h-4 w-4" />
                    <span className="truncate">{item.name}</span>
                    {item.id === organization?.id && (
                      <Badge variant="secondary" className="ml-auto h-4 py-0 text-[10px]">
                        {t('shell:sidebar.current')}
                      </Badge>
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <SidebarMenuButton
              size="lg"
              tooltip={cabinetName}
              className={cabinetButtonClass}
              data-testid="button-sidebar-organization"
            >
              <CabinetTile />
              <CabinetLabel name={cabinetName} />
            </SidebarMenuButton>
          )}
        </SidebarMenuItem>
        <SidebarMenuItem>
          <PlanBadge />
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarFooter>
  );
}

/**
 * L'offre du cabinet, en pied de barre. Elle mène à la section Abonnement plutôt que de rester
 * décorative : le praticien qui bute sur un quota doit pouvoir savoir d'où il vient. Rien n'est
 * rendu tant que la lecture n'a pas abouti — un badge affiché par défaut mentirait.
 */
function PlanBadge() {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const entitlements = useEntitlements();
  if (entitlements.data === undefined) return null;

  const trialing = entitlements.data.status === 'trialing';
  return (
    <SidebarMenuButton
      onClick={() => {
        navigate('/settings/subscription');
      }}
      tooltip={t(`plans:names.${entitlements.data.plan}`)}
      data-testid="button-sidebar-plan"
    >
      <CreditCard className="h-4 w-4" />
      <span className="truncate text-xs text-muted-foreground">{t('plans:section.current')}</span>
      <Badge
        variant={trialing ? 'secondary' : 'default'}
        className="ml-auto h-4 py-0 text-[10px]"
        data-testid="badge-sidebar-plan"
      >
        {t(`plans:names.${entitlements.data.plan}`)}
      </Badge>
    </SidebarMenuButton>
  );
}
